import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  AccessTime as TimeIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Appointment {
  id: string;
  title: string;
  date: Date;
  startTime: string;
  endTime: string;
  customerName: string;
  vehicleInfo: {
    make: string;
    model: string;
    year: number;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

interface TimeSlot {
  time: string;
  appointments: Appointment[];
}

export const ScheduleManagement: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Mock appointments data
  const appointments: Appointment[] = [
    {
      id: '1',
      title: 'Oil Change',
      date: new Date(2023, 2, 15),
      startTime: '09:00',
      endTime: '10:00',
      customerName: 'John Smith',
      vehicleInfo: {
        make: 'Toyota',
        model: 'Corolla',
        year: 2019,
      },
      status: 'scheduled',
    },
    {
      id: '2',
      title: 'Brake Inspection',
      date: new Date(2023, 2, 15),
      startTime: '10:30',
      endTime: '11:30',
      customerName: 'Sarah Johnson',
      vehicleInfo: {
        make: 'Honda',
        model: 'Civic',
        year: 2020,
      },
      status: 'scheduled',
    },
    {
      id: '3',
      title: 'Engine Diagnostic',
      date: new Date(2023, 2, 15),
      startTime: '13:00',
      endTime: '15:00',
      customerName: 'Michael Brown',
      vehicleInfo: {
        make: 'BMW',
        model: 'X5',
        year: 2021,
      },
      status: 'in-progress',
    },
  ];

  // Generate time slots for the day view
  const generateTimeSlots = () => {
    const slots: TimeSlot[] = [];
    for (let hour = 8; hour < 18; hour++) {
      const hourString = hour.toString().padStart(2, '0');
      slots.push({
        time: `${hourString}:00`,
        appointments: [],
      });
      slots.push({
        time: `${hourString}:30`,
        appointments: [],
      });
    }

    // Filter appointments for the current date
    const currentDateString = currentDate.toDateString();
    const todayAppointments = appointments.filter(
      app => new Date(app.date).toDateString() === currentDateString
    );

    // Assign appointments to time slots
    todayAppointments.forEach(appointment => {
      const slotIndex = slots.findIndex(slot => slot.time === appointment.startTime);
      if (slotIndex >= 0) {
        slots[slotIndex].appointments.push(appointment);
      }
    });

    return slots;
  };

  const timeSlots = generateTimeSlots();

  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'in-progress':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Schedule Management
        </Typography>

        {/* Calendar Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handlePreviousDay}>
            <ChevronLeftIcon />
          </IconButton>
          <Button startIcon={<TodayIcon />} onClick={handleToday} sx={{ mx: 1 }}>
            Today
          </Button>
          <IconButton onClick={handleNextDay}>
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
            {formatDate(currentDate)}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
          >
            New Appointment
          </Button>
        </Box>

        {/* Day View */}
        <Grid container spacing={2}>
          {/* Time Slots */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              {timeSlots.map((slot, index) => (
                <Box key={index}>
                  <Box
                    sx={{
                      display: 'flex',
                      borderTop: index > 0 ? 1 : 0,
                      borderColor: 'divider',
                      py: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {slot.time}
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {slot.appointments.length > 0 ? (
                        slot.appointments.map(appointment => (
                          <Card
                            key={appointment.id}
                            sx={{
                              width: 300,
                              cursor: 'pointer',
                              '&:hover': { boxShadow: 3 },
                              bgcolor:
                                appointment.status === 'in-progress'
                                  ? 'warning.light'
                                  : appointment.status === 'completed'
                                  ? 'success.light'
                                  : appointment.status === 'cancelled'
                                  ? 'error.light'
                                  : 'primary.light',
                            }}
                          >
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                  {appointment.title}
                                </Typography>
                                <Chip
                                  label={
                                    appointment.status.charAt(0).toUpperCase() +
                                    appointment.status.slice(1)
                                  }
                                  size="small"
                                  color={getStatusColor(appointment.status) as any}
                                />
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <TimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="body2">
                                  {appointment.startTime} - {appointment.endTime}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="body2">{appointment.customerName}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CarIcon fontSize="small" sx={{ mr: 0.5 }} />
                                <Typography variant="body2">
                                  {appointment.vehicleInfo.make} {appointment.vehicleInfo.model} (
                                  {appointment.vehicleInfo.year})
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}; 